import { useEffect, useState } from 'react';

import STORE from 'store';
import useCurrentAccountId from 'hooks/useCurrentAccountId';

// Loads the schedules for a single piece of content. Schedules are queried by
// content id (stored in the `reportId` field for both reports and dashboards);
// contentType is filtered client-side because legacy report schedules have no
// contentType field (absent ⇒ 'report'), which a Firestore where-clause would
// wrongly exclude. Content ids are globally unique, so the id match alone is
// already near-exclusive — the type filter is defence-in-depth.
const useReportSchedules = ({
  contentId,
  contentType,
}: {
  contentId: string;
  contentType: ReportSchedules.ContentType;
}) => {
  const accountId = useCurrentAccountId();
  const [schedules, setSchedules] = useState<ReportSchedules.Schedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const unsubscribe = STORE.contentDistributions
      .getReportSchedulesRef({ accountId })
      .where('reportId', '==', contentId)
      .orderBy('createdOn', 'desc')
      .onSnapshot((snapshot) => {
        if (!isActive) return;
        setSchedules(
          snapshot.docs
            .map((d) => d.data())
            .filter((s) => (s.contentType ?? 'report') === contentType),
        );
        setIsLoading(false);
      });

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [accountId, contentId, contentType]);

  return { schedules, isLoading };
};

export default useReportSchedules;
